// IndexedDB access shared by the page and the service worker; every call degrades to a no-op.

const DB_NAME = "vantero.menu.offline"
const DB_VERSION = 1

export const STORES = {
  SNAPSHOTS: "snapshots",
  OUTBOX: "outbox",
  META: "meta",
  IMAGE_LRU: "imageLru"
}

let dbPromise = null

function openDb() {
  if (dbPromise) return dbPromise

  const opening = new Promise((resolve, reject) => {
    const factory = globalThis.indexedDB
    if (!factory) {
      reject(new Error("IndexedDB unavailable"))
      return
    }

    const request = factory.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORES.SNAPSHOTS)) db.createObjectStore(STORES.SNAPSHOTS)
      if (!db.objectStoreNames.contains(STORES.OUTBOX)) {
        db.createObjectStore(STORES.OUTBOX, { keyPath: "id", autoIncrement: true })
      }
      if (!db.objectStoreNames.contains(STORES.META)) db.createObjectStore(STORES.META)
      if (!db.objectStoreNames.contains(STORES.IMAGE_LRU)) db.createObjectStore(STORES.IMAGE_LRU)
    }
    request.onsuccess = () => {
      const db = request.result
      // Lets a newer app version upgrade the schema instead of waiting on this connection.
      db.onversionchange = () => {
        db.close()
        dbPromise = null
      }
      resolve(db)
    }
    request.onerror = () => reject(request.error)
  })

  dbPromise = opening
  // A failed open is retried on the next call.
  opening.catch(() => {
    if (dbPromise === opening) dbPromise = null
  })
  return opening
}

// Runs one unit of work in its own transaction and resolves once it has committed.
function run(storeName, mode, work) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, mode)
        let result
        const request = work(tx.objectStore(storeName))
        if (request) {
          request.onsuccess = () => {
            result = request.result
          }
        }
        tx.oncomplete = () => resolve(result)
        tx.onerror = () => reject(tx.error)
        tx.onabort = () => reject(tx.error)
      })
  )
}

export async function idbGet(storeName, key) {
  try {
    return (await run(storeName, "readonly", (store) => store.get(key))) ?? null
  } catch {
    return null
  }
}

export async function idbGetAll(storeName) {
  try {
    return (await run(storeName, "readonly", (store) => store.getAll())) ?? []
  } catch {
    return []
  }
}

export async function idbGetAllKeys(storeName) {
  try {
    return (await run(storeName, "readonly", (store) => store.getAllKeys())) ?? []
  } catch {
    return []
  }
}

// Key/value pairs read in a single transaction, so keys and values always line up.
export async function idbEntries(storeName) {
  const entries = []
  try {
    await run(storeName, "readonly", (store) => {
      const cursorRequest = store.openCursor()
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result
        if (!cursor) return
        entries.push({ key: cursor.key, value: cursor.value })
        cursor.continue()
      }
      return null
    })
    return entries
  } catch {
    return []
  }
}

// Resolves to the stored key, or null when the write did not happen.
export async function idbPut(storeName, value, key) {
  try {
    const stored = await run(storeName, "readwrite", (store) =>
      key === undefined ? store.put(value) : store.put(value, key)
    )
    return stored ?? null
  } catch {
    return null
  }
}

export async function idbDelete(storeName, key) {
  try {
    await run(storeName, "readwrite", (store) => store.delete(key))
    return true
  } catch {
    return false
  }
}

export async function idbClear(storeName) {
  try {
    await run(storeName, "readwrite", (store) => store.clear())
    return true
  } catch {
    return false
  }
}
